import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import "../../quinn.css";
import {
    CalendarDaysIcon,
    ChartBarIcon,
    CheckIcon,
    ChevronDownIcon,
    DocumentMagnifyingGlassIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    MinusIcon,
    PencilSquareIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import { QuinnTraceStep, QuinnTraceStepKind, QuinnTraceStepStatus } from "../../types";

const STEP_ICON: Record<QuinnTraceStepKind, typeof SparklesIcon> = {
    [QuinnTraceStepKind.Interpret]: SparklesIcon,
    [QuinnTraceStepKind.SearchTransactions]: MagnifyingGlassIcon,
    [QuinnTraceStepKind.MonthlyOverview]: CalendarDaysIcon,
    [QuinnTraceStepKind.Forecast]: ChartBarIcon,
    [QuinnTraceStepKind.DocumentSearch]: DocumentMagnifyingGlassIcon,
    [QuinnTraceStepKind.Reasoning]: SparklesIcon,
    [QuinnTraceStepKind.Compose]: PencilSquareIcon,
};

function formatDuration(totalMs: number): string {
    if (totalMs < 1000) return `${totalMs} ms`;
    return `${(totalMs / 1000).toLocaleString("nl-NL", { maximumFractionDigits: 1 })}s`;
}

function StatusMark({ status }: { status: QuinnTraceStep["status"] }) {
    switch (status) {
        case QuinnTraceStepStatus.Running:
            return (
                <span className="flex h-4 w-4 items-center justify-center rounded-full border border-dashed border-primary">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                </span>
            );
        case QuinnTraceStepStatus.Failed:
            return (
                <span className="flex h-4 w-4 items-center justify-center rounded-full border border-rose-400 text-rose-500">
                    <ExclamationTriangleIcon className="h-2.5 w-2.5" />
                </span>
            );
        case QuinnTraceStepStatus.Skipped:
            return (
                <span className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-gray-400">
                    <MinusIcon className="h-2.5 w-2.5" />
                </span>
            );
        case QuinnTraceStepStatus.Done:
            return (
                <span className="flex h-4 w-4 items-center justify-center rounded-full border border-emerald-500 text-emerald-600">
                    <CheckIcon className="h-2.5 w-2.5" />
                </span>
            );
        default: {
            const _exhaustive: never = status;
            return _exhaustive;
        }
    }
}

export function QuinnTrace({ steps, isStreaming }: { steps: QuinnTraceStep[]; isStreaming: boolean }) {
    const prefersReducedMotion = useReducedMotion();
    const [isExpanded, setIsExpanded] = useState(true);

    useEffect(() => {
        if (!isStreaming) setIsExpanded(false);
    }, [isStreaming]);

    if (steps.length === 0) return null;

    const totalMs = steps.reduce((total, step) => total + (step.durationMs ?? 0), 0);
    const failedCount = steps.filter((step) => step.status === QuinnTraceStepStatus.Failed).length;
    const runningStep = steps.find((step) => step.status === QuinnTraceStepStatus.Running);

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/80">
            <button
                type="button"
                onClick={() => setIsExpanded((current) => !current)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
                aria-expanded={isExpanded}
            >
                {isStreaming ? (
                    <span className="relative flex h-2 w-2 shrink-0">
                        {!prefersReducedMotion && (
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                        )}
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                ) : (
                    <span className={`h-2 w-2 shrink-0 rounded-full ${failedCount ? "bg-amber-500" : "bg-emerald-500"}`} />
                )}

                <span className="min-w-0 flex-1 truncate text-[11px] font-medium uppercase tracking-wider text-gray-500">
                    {isStreaming && runningStep ? (
                        <span className={prefersReducedMotion ? "" : "quinn-shimmer"}>{runningStep.label}…</span>
                    ) : (
                        <>
                            {steps.length} {steps.length === 1 ? "stap" : "stappen"}
                            {totalMs > 0 && <span className="text-gray-400"> · {formatDuration(totalMs)}</span>}
                            {failedCount > 0 && <span className="text-amber-600"> · {failedCount} mislukt</span>}
                        </>
                    )}
                </span>

                <ChevronDownIcon
                    className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                />
            </button>

            <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        initial={prefersReducedMotion ? false : { height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={prefersReducedMotion ? undefined : { height: 0, opacity: 0 }}
                        transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
                        className="overflow-hidden"
                    >
                        <ol className="space-y-0 px-3 pb-2">
                            {steps.map((step, index) => {
                                const Icon = STEP_ICON[step.kind] ?? SparklesIcon;
                                const isLast = index === steps.length - 1;
                                return (
                                    <motion.li
                                        key={step.id}
                                        initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.22, ease: "easeOut" }}
                                        className="relative flex gap-2.5 pb-2 pl-0"
                                    >
                                        {!isLast && <span className="absolute left-[7px] top-5 h-full w-px bg-gray-200" />}
                                        <span className="relative z-10 mt-0.5 bg-gray-50/80">
                                            <StatusMark status={step.status} />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-center gap-1.5">
                                                <Icon className="h-3 w-3 shrink-0 text-gray-400" />
                                                <span className="truncate text-xs font-medium text-gray-800">{step.label}</span>
                                                {step.durationMs != null && step.durationMs > 0 && (
                                                    <span className="ml-auto shrink-0 text-[10px] tabular-nums text-gray-400">
                                                        {formatDuration(step.durationMs)}
                                                    </span>
                                                )}
                                            </span>
                                            {step.detail && <span className="mt-0.5 block truncate text-[11px] text-gray-500">{step.detail}</span>}
                                        </span>
                                    </motion.li>
                                );
                            })}
                        </ol>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
